/* eslint-disable max-lines */

import {
 useState, useEffect,
 JSX
} from 'react';
import PageBuilder from './PageBuilder';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import OpCodeConfiguration from './OpCodeConfiguration';
import OpCodePreview from './OpCodePreview';
import ChatSection from './ChatSection';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Checkbox
} from 'shared/components/ui/checkbox';
import {
    Button
} from 'shared/components/ui/button';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from 'shared/components/ui/accordion';

// Define types for props
interface MainContentProps {
    activeBuilder: 'opcode' | 'page';
    selectedItem: any; // Replace 'any' with a proper type for selectedItem
    opCodes: any[]; // Replace 'any' with the correct type for opCodes
    setPages: React.Dispatch<React.SetStateAction<any[]>>; // Replace 'any' with the type for pages
    handleSaveSchema: () => void;
    handleSaveChanges: () => void;
    setSelectedItem: React.Dispatch<React.SetStateAction<any>>; // Replace 'any' with the correct type
    assistantResponse: string | null;
    setAssistantResponse: React.Dispatch<React.SetStateAction<string | null>>;
    jsonBlocks: any[]; // Replace 'any' with the proper type
    setJsonBlocks: React.Dispatch<React.SetStateAction<any[]>>; // Replace 'any' with the proper type
    isSaving: boolean;
    generatePreviewPayload: (config: any, databaseSchema: any, selectedMainTable: string) => any; // Replace 'any' with the correct types
    showValidationErrors: boolean;
    previewBlocks: any; // Replace 'any' with the proper type
    setPreviewBlocks: React.Dispatch<React.SetStateAction<any>>; // Replace 'any' with the correct type
}

// Helper function for extracting JSON after the 'Schema:'
function extractJsonAfterSchema(text: string): any | null {
    const schemaIndex = text.indexOf('Schema:');
    if (schemaIndex === -1) return null;

    const braceStart = text.indexOf('{', schemaIndex);
    if (braceStart === -1) return null;

    let braceCount = 0;
    let i = braceStart;

    while (i < text.length) {
        if (text[i] === '{') braceCount++;
        if (text[i] === '}') braceCount--;

        if (braceCount === 0) {
            const jsonText = text.slice(braceStart, i + 1);
            try {
                return JSON.parse(jsonText);
            } catch (e) {
                console.error('Failed to parse extracted JSON:', e);
                return null;
            }
        }

        i++;
    }

    return null;
}

export default function MainContent({
    activeBuilder,
    selectedItem,
    opCodes,
    setPages,
    handleSaveSchema,
    handleSaveChanges,
    setSelectedItem,
    assistantResponse,
    setAssistantResponse,
    jsonBlocks,
    setJsonBlocks,
    isSaving,
    generatePreviewPayload,
    showValidationErrors,
    previewBlocks,
    setPreviewBlocks
}: MainContentProps): JSX.Element {
    const [activeTab, setActiveTab] = useState<string>('configure');
    const [selectedConnectedTables, setSelectedConnectedTables] = useState<string[]>(['Users']);

    // Assistant response area
    // useEffect(() => {
    //     let parsedBlocks: any[] = [];

    //     if (!assistantResponse) {
    //         setJsonBlocks([]);
    //         return;
    //     }

    //     // Match ```json ... ``` blocks
    //     const matches = [...assistantResponse.matchAll(/```json\s*([\s\S]*?)\s*```/g)];
    //     if (matches.length > 0) {
    //         parsedBlocks = matches.map((match) => {
    //             try {
    //                 return JSON.parse(match[1]);
    //             } catch (err) {
    //                 console.error('Invalid JSON block:', err);
    //                 return null;
    //             }
    //         });
    //     } else {
    //         // New robust fallback using brace-counting
    //         const schemaJson = extractJsonAfterSchema(assistantResponse);
    //         if (schemaJson) {
    //             parsedBlocks = [schemaJson];
    //         }
    //     }
    //     setJsonBlocks(parsedBlocks.filter(Boolean));
    // }, [assistantResponse]);
    useEffect(() => {
    let parsedBlocks: any[] = [];

    if (!assistantResponse) {
        setJsonBlocks([]);
        return;
    }

    // Match ```json ... ``` blocks
    const regex = /```json\s*([\s\S]*?)\s*```/g;
    const matches = [];
    let match;
    while ((match = regex.exec(assistantResponse)) !== null) {
        matches.push(match);
    }

    if (matches.length > 0) {
        parsedBlocks = matches.map((match) => {
            try {
                return JSON.parse(match[1]);
            } catch (err) {
                console.error('Invalid JSON block:', err);
                return null;
            }
        });
    } else {
        // New robust fallback using brace-counting
        const schemaJson = extractJsonAfterSchema(assistantResponse);
        if (schemaJson) {
            parsedBlocks = [schemaJson];
        }
    }
    setJsonBlocks(parsedBlocks.filter(Boolean));
}, [assistantResponse]);

    useEffect(() => {
        if (!jsonBlocks || jsonBlocks.length === 0) return;

        const schemaBlock = jsonBlocks.find((block) => block?.databaseSchema && block?.config);

        if (schemaBlock && selectedItem) {
            // Normalize field types
            const tables = schemaBlock.databaseSchema.tables;
            // Normalize and flatten connectedTables into top-level databaseSchema.tables
            for (const tableName in tables) {
                const table = tables[tableName];
                table.fields = table.fields.map((field) => ({
                    ...field,
                    type: field.type || field.dataType || 'string',
                }));

                // Flatten inline connectedTables into top-level schema
                if (Array.isArray(table.connectedTables)) {
                    table.connectedTables.forEach((connected) => {
                        const connectedName = typeof connected === 'string' ? connected : connected.name;
                        const connectedFields = typeof connected === 'object' && connected.fields ? connected.fields : [];

                        if (!tables[connectedName]) {
                            tables[connectedName] = {
                                name: connectedName,
                                fields: connectedFields.map((f) => ({
                                    ...f,
                                    type: f.type || f.dataType || 'string'
                                })),
                                connectedTables: [tableName] // basic backref
                            };
                        }
                    });
                }
            }

            // Merge into existing selectedItem instead of replacing
            const merged = {
                ...selectedItem,
                config: {
                    ...selectedItem.config,
                    ...schemaBlock.config,
                    fieldSettings: {
                        ...(selectedItem.config.fieldSettings || {}),
                        ...(schemaBlock.config.fieldSettings || {})
                    },
                },
                databaseSchema: {
                    ...schemaBlock.databaseSchema,
                },
            };

            setSelectedItem(merged);
            setSelectedConnectedTables(schemaBlock.config.selectedConnectedTables || []);
            const preview = generatePreviewPayload(
                merged.config,
                merged.databaseSchema,
                merged.config.selectedMainTable
            );
            setPreviewBlocks(preview);
        }
    }, [jsonBlocks]);

    // Handle changes for table fields visibility and selected tables
    const handleFieldVisibilityChange = (tableName: string, fieldName: string, newValue: string) => {
        const fieldKey = `${tableName}.${fieldName}`;
        const isMainTable = tableName === selectedItem.config.selectedMainTable;

        const updatedFieldSettings = {
            ...(selectedItem.config.fieldSettings || {}),
            [fieldKey]: newValue
        };

        let updatedSelectedFields = [...selectedItem.config.selectedFields];
        let updatedConnectedFields = [...selectedItem.config.selectedConnectedFields];

        if (isMainTable) {
            if (!updatedSelectedFields.includes(fieldKey)) {
                updatedSelectedFields.push(fieldKey);
            }
        } else {
            if (!updatedConnectedFields.includes(fieldKey)) {
                updatedConnectedFields.push(fieldKey);
            }
        }

        setSelectedItem({
            ...selectedItem,
            config: {
                ...selectedItem.config,
                fieldSettings: updatedFieldSettings,
                selectedFields: updatedSelectedFields,
                selectedConnectedFields: updatedConnectedFields
            }
        });
    };

    // Handle assistant response received
    const handleResponseReceived = (llmResponse: string) => {
        setAssistantResponse(llmResponse);
    };

    const handleMainTableChange = (tableName: string) => {
        setSelectedItem({
            ...selectedItem,
            config: {
                ...selectedItem.config,
                selectedMainTable: tableName,
                selectedFields: [],
                selectedConnectedTables: [],
                selectedConnectedFields: [],
            },
        });
        setSelectedConnectedTables([]);
    };

    const handleConnectedTableChange = (tableName: string) => {
        const updatedConnectedTables = selectedConnectedTables.includes(tableName)
            ? selectedConnectedTables.filter((t) => t !== tableName)
            : [...selectedConnectedTables, tableName];

        setSelectedConnectedTables(updatedConnectedTables);

        setSelectedItem({
            ...selectedItem,
            config: {
                ...selectedItem.config,
                selectedConnectedTables: updatedConnectedTables,
                selectedConnectedFields: selectedItem.config.selectedConnectedFields.filter((field) =>
                    updatedConnectedTables.some((t) => field.startsWith(t))
                ),
            },
        });
    };

    const handleFieldChange = (tableName: string, fieldName: string, isChecked: boolean) => {
        const fullFieldName = `${tableName}.${fieldName}`;
        const isMainTable = tableName === selectedItem.config.selectedMainTable;

        const updatedFieldSettings = {
            ...(selectedItem.config.fieldSettings || {})
        };

        let updatedFields = isMainTable
            ? [...selectedItem.config.selectedFields]
            : [...selectedItem.config.selectedConnectedFields];

        if (isChecked) {
            if (!updatedFields.includes(fullFieldName)) {
                updatedFields.push(fullFieldName);
            }
            if (!updatedFieldSettings[fullFieldName]) {
                updatedFieldSettings[fullFieldName] = 'visible'; // default to visible on check
            }
        } else {
            updatedFields = updatedFields.filter((f) => f !== fullFieldName);
            delete updatedFieldSettings[fullFieldName]; // remove setting when unchecked
        }

        const newConfig = {
            ...selectedItem.config,
            fieldSettings: updatedFieldSettings,
            ...(isMainTable
                ? {
                    selectedFields: updatedFields
                }
                : {
                    selectedConnectedFields: updatedFields
                }),
        };

        const updatedItem = {
            ...selectedItem,
            config: newConfig,
        };

        setSelectedItem(updatedItem);

        setPreviewBlocks(generatePreviewPayload(newConfig, updatedItem.databaseSchema, updatedItem.config.selectedMainTable));
    };

    return (
        <div className="flex-1 flex w-full">
            {activeBuilder === 'opcode' && selectedItem && 'dataType' in selectedItem ? (
                <div className="flex-1 flex w-full">
                    <ChatSection onResponseReceived={handleResponseReceived} />
                    <div className="w-2/3 bg-background">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-10">
                            <TabsList className="h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground w-full grid grid-cols-3 max-w-md mx-auto">
                                <TabsTrigger value="configure">Configure</TabsTrigger>
                                <TabsTrigger value="schema">Database Schema</TabsTrigger>
                                <TabsTrigger value="preview">Preview</TabsTrigger>
                            </TabsList>
                            <TabsContent value="configure" className="p-4">
                                <ScrollArea className="h-[calc(100vh-250px)] w-full">
                                    <OpCodeConfiguration
                                        selectedItem={selectedItem}
                                        setSelectedItem={setSelectedItem}
                                        handleSaveChanges={handleSaveChanges}
                                    />
                                </ScrollArea>
                            </TabsContent>
                            <TabsContent value="schema" className="p-4">
                                <ScrollArea className="h-[calc(100vh-250px)] w-full">
                                    <div className="p-4">
                                        <Accordion type="multiple" className="w-full space-y-2">
                                            {/* Accordion Item 1: Extracted JSON */}
                                            <AccordionItem value="json-blocks">
                                                <AccordionTrigger className="text-left font-semibold">
                                                    Extracted JSON Blocks
                                                </AccordionTrigger>
                                                <AccordionContent>
                                                    {jsonBlocks.length > 0 ? (
                                                        jsonBlocks.map((block, i) => (
                                                            <div
                                                                key={i}
                                                                className="mb-4 p-4 bg-gray-100 rounded border overflow-x-auto"
                                                            >
                                                                <pre className="text-sm whitespace-pre-wrap break-words font-mono">
                                                                    <code>{JSON.stringify(block, null, 2)}</code>
                                                                </pre>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <p className="text-gray-600">No valid JSON blocks found.</p>
                                                    )}
                                                </AccordionContent>
                                            </AccordionItem>
                                            {/* Accordion Item 2: Preview Table JSON */}
                                            <AccordionItem value="preview-blocks">
                                                <AccordionTrigger className="text-left font-semibold">
                                                    Preview Table JSON
                                                </AccordionTrigger>
                                                <AccordionContent>
                                                    {previewBlocks ? (
                                                        <div className="flex flex-col w-full mb-4 p-4 rounded border overflow-x-auto">
                                                            <pre className="text-sm whitespace-pre-wrap break-words font-mono">
                                                                <code>{JSON.stringify(previewBlocks, null, 2)}</code>
                                                            </pre>
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-600">No valid preview JSON blocks found.</p>
                                                    )}
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    </div>

                                    <div className="p-4">
                                        <div>
                                            <Label className="text-base">Select Main Table</Label>
                                            <Select
                                                value={selectedItem.config.selectedMainTable || ''}
                                                onValueChange={handleMainTableChange}
                                            >
                                                <SelectTrigger className="w-full mt-1.5">
                                                    <SelectValue placeholder="Select a table" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.keys(selectedItem.databaseSchema.tables).map((tableName) => (
                                                        <SelectItem key={tableName} value={tableName}>
                                                            {tableName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {selectedItem.config.selectedMainTable && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label className="text-base">Fields from {selectedItem.config.selectedMainTable}</Label>
                                                    <div className="space-y-2 mt-1.5">
                                                        {selectedItem.databaseSchema.tables[selectedItem.config.selectedMainTable]?.fields?.map(
                                                            (field) => (
                                                                <div key={field.name} className="flex items-center space-x-2">
                                                                    <Checkbox
                                                                        id={`${selectedItem.config.selectedMainTable}.${field.name}`}
                                                                        checked={selectedItem.config.selectedFields.includes(
                                                                            `${selectedItem.config.selectedMainTable}.${field.name}`,
                                                                        )}
                                                                        // onCheckedChange={(checked) =>
                                                                        //     handleFieldChange(selectedItem.config.selectedMainTable, field.name, checked)
                                                                        // }
                                                                        onCheckedChange={(checked) =>
    handleFieldChange(selectedItem.config.selectedMainTable, field.name, !!checked)
}
                                                                    />

                                                                    <Label
                                                                        htmlFor={`${selectedItem.config.selectedMainTable}.${field.name}`}
                                                                        className="text-sm font-normal"
                                                                    >
                                                                        {field.name} ({field.dataType})
                                                                    </Label>
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-base">Connected Tables</Label>
                                                    <div className="space-y-4 mt-1.5">
                                                        {
                                                            (selectedItem.databaseSchema.tables[selectedItem.config.selectedMainTable]?.connectedTables || []).map((tableRef) => {
                                                                const tableName = typeof tableRef === 'string' ? tableRef : tableRef.name;
                                                                const tableFields =
                                                                typeof tableRef === 'object' && tableRef.fields
                                                                    ? tableRef.fields
                                                                    : selectedItem.databaseSchema.tables[tableName]?.fields || [];

                                                                return (
                                                                    <div key={tableName} className="space-y-2">
                                                                        <div className="flex items-center space-x-2">
                                                                            <Checkbox
                                                                                id={`connected-${tableName}`}
                                                                                checked={selectedConnectedTables.includes(tableName)}
                                                                                onCheckedChange={() => handleConnectedTableChange(tableName)}
                                                                            />
                                                                            <Label htmlFor={`connected-${tableName}`} className="text-sm font-normal">
                                                                                {tableName}
                                                                            </Label>
                                                                        </div>

                                                                        {selectedConnectedTables.includes(tableName) && (
                                                                            <div className="ml-6 space-y-2">
                                                                                {tableFields.map((field) => (
                                                                                    <div key={`${tableName}.${field.name}`} className="flex items-center space-x-2">
                                                                                        <Checkbox
                                                                                            id={`${tableName}.${field.name}`}
                                                                                            checked={selectedItem.config.selectedConnectedFields.includes(`${tableName}.${field.name}`)}
                                                                                            // onCheckedChange={(checked) => handleFieldChange(tableName, field.name, checked)}
                                                                                            onCheckedChange={(checked) => handleFieldChange(tableName, field.name, Boolean(checked))}

                                                                                        />
                                                                                        <Label htmlFor={`${tableName}.${field.name}`} className="text-sm font-normal">
                                                                                            {field.name} ({field.dataType})
                                                                                        </Label>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </div>

                                                <Button
                                                    className="w-full bg-burgundy text-burgundy-foreground hover:bg-burgundy/90"
                                                    onClick={handleSaveSchema}
                                                    disabled={isSaving}
                                                >
                                                    Save Schema
                                                </Button>
                                            </>
                                        )}
                                    </div>

                                    <div className="p-4">
                                        {selectedItem?.config?.selectedMainTable && selectedItem?.databaseSchema ? (
                                            <>
                                                {/* Main Table */}
                                                <h2 className="text-xl font-bold mb-4">Main Table: {selectedItem.config.selectedMainTable}</h2>
                                                {selectedItem.databaseSchema.tables[selectedItem.config.selectedMainTable]?.fields
                                                    .filter((field) =>
                                                        selectedItem.config.selectedFields.includes(`${selectedItem.config.selectedMainTable}.${field.name}`)
                                                    )
                                                    .map((field, index) => {
                                                        const fieldKey = `${selectedItem.config.selectedMainTable}.${field.name}`;
                                                        const value = selectedItem.config.fieldSettings?.[fieldKey] || '';
                                                        const isInvalid = showValidationErrors && !['visible', 'hidden', 'readonly'].includes(value);

                                                        return (
                                                            <div key={index} className="flex flex-col gap-1 mb-3">
                                                                <div className="flex items-center gap-4">
                                                                    <Label className="w-1/2 font-mono">
                                                                        {field.name}{' '}
                                                                        <span className="text-xs text-gray-500">({field.dataType})</span>
                                                                    </Label>
                                                                    <select
                                                                        className={`w-1/2 p-1 border rounded ${isInvalid ? 'border-red-500' : 'border-gray-300'}`}
                                                                        value={value}
                                                                        onChange={(e) =>
                                                                            handleFieldVisibilityChange(selectedItem.config.selectedMainTable, field.name, e.target.value)
                                                                        }
                                                                    >
                                                                        <option value="">Select an option</option>
                                                                        <option value="visible">Visible</option>
                                                                        <option value="hidden">Hidden</option>
                                                                        <option value="readonly">Read Only</option>
                                                                    </select>
                                                                </div>

                                                                {isInvalid && (
                                                                    <p className="text-sm text-red-600 ml-auto pr-1">Please select a visibility option</p>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                {/* Connected Tables */}
                                                {selectedItem.config.selectedConnectedTables.length > 0 && (
                                                    <>
                                                        <h2 className="text-xl font-bold mt-8 mb-4">Connected Tables</h2>
                                                        {selectedItem.config.selectedConnectedTables.map((tableName, idx) => {
                                                            const tableFields = selectedItem.databaseSchema.tables[tableName]?.fields || [];
                                                            return (
                                                                <div key={idx} className="mb-6">
                                                                    <h3 className="text-lg font-semibold mb-2">{tableName}</h3>
                                                                    {tableFields
                                                                        .filter((field) =>
                                                                            selectedItem.config.selectedConnectedFields.includes(`${tableName}.${field.name}`)
                                                                        )
                                                                        .map((field, i) => {
                                                                            const fieldKey = `${tableName}.${field.name}`;
                                                                            const value = selectedItem.config.fieldSettings?.[fieldKey] || '';
                                                                            const isInvalid = showValidationErrors && !['visible', 'hidden', 'readonly'].includes(value);

                                                                            return (
                                                                                <div key={i} className="flex flex-col gap-1 mb-3">
                                                                                    <div className="flex items-center gap-4">
                                                                                        <label className="w-1/2 font-mono">
                                                                                            {field.name} <span className="text-xs text-gray-500">({field.dataType})</span>
                                                                                        </label>
                                                                                        <select
                                                                                            className={`w-1/2 p-1 border rounded ${isInvalid ? 'border-red-500' : 'border-gray-300'}`}
                                                                                            value={value}
                                                                                            onChange={(e) =>
                                                                                                handleFieldVisibilityChange(tableName, field.name, e.target.value)
                                                                                            }
                                                                                        >
                                                                                            <option value="">Select an option</option>
                                                                                            <option value="visible">Visible</option>
                                                                                            <option value="hidden">Hidden</option>
                                                                                            <option value="readonly">Read Only</option>
                                                                                        </select>
                                                                                    </div>
                                                                                    {isInvalid && (
                                                                                        <p className="text-sm text-red-600 ml-auto pr-1">
                                                                                            Please select a visibility option
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-gray-600">No valid table schema available.</p>
                                        )}
                                    </div>

                                </ScrollArea>
                            </TabsContent>
                            <TabsContent value="preview" className="p-4">
                                <ScrollArea className="h-[calc(100vh-250px)] w-full">
                                    <OpCodePreview selectedItem={selectedItem}/>
                                </ScrollArea>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            ) : activeBuilder === 'page' && selectedItem && 'title' in selectedItem ? (
                <PageBuilder
                    availableOpCodes={opCodes}
                    initialSections={selectedItem.sections || []}
                    onSave={(sections) => {
                        const updatedPage = {
                            ...selectedItem,
                            sections
                        };
                        setPages((prev) => prev.map((p) => (p.id === selectedItem.id ? updatedPage : p)));
                    }}
                />
            ) : (
                <div className="flex-1 p-4">
                    <h2 className="text-2xl font-bold mb-4">{activeBuilder === 'opcode' ? 'OpCode Builder' : 'Page Builder'}</h2>
                    <p className="text-muted-foreground">
                        {activeBuilder === 'opcode'
                            ? 'Select an OpCode from the sidebar to edit or create a new OpCode.'
                            : 'Select a page from the sidebar to edit or create a new page.'}
                    </p>
                </div>
            )}
        </div>
    );
}